import React, { useEffect, useMemo } from 'react';
import {
  ReferenceField,
  Datagrid,
  TextField,
  DatagridBody,
  useUnselectAll,
  FunctionField
} from 'react-admin';
import { connect } from 'react-redux';
import { get } from 'lodash';

import CircularProgress from '@material-ui/core/CircularProgress';
import { withStyles } from '@material-ui/core/styles';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Checkbox from '@material-ui/core/Checkbox';

import LinkField from '../../core/components/LinkField';
import { categoryStatus } from '../categories/CategoryLookups';
import StatusSelectField from '../../core/components/StatusSelectField';

const styles = () => ({
  statusColumnStyle: {
    width: '68px'
  },
  label: {
    color: '#424242',
    fontSize: '14px',
    display: 'inline-block',
    paddingBottom: '10px'
  },
  expandLabel: {
    color: '#757575',
    fontSize: '14px',
    display: 'inline-block',
    transform: 'translateY(-10px)'
  },
  progress: {
    display: 'flex',
    justifyContent: 'center'
  },
  editButton: {
    color: '#757575',
    minWidth: '24px',
    padding: '6px 0',
    '& span': {
      paddingRight: 0
    }
  },
  editButtonColumnStyle: {
    width: '24px'
  }
});

const CustomDatagridRow = ({
  record,
  resource,
  id,
  children,
  basePath,
  selected,
  onToggleItem,
  hasBulkActions
}) => {
  return (
    <TableRow key={id}>
      {/* first column: selection checkbox */}
      {hasBulkActions && (
        <TableCell padding="none">
          <Checkbox
            checked={selected}
            onClick={event => onToggleItem(id, event)}
            style={{ padding: 0 }}
          />
        </TableCell>
      )}

      {/* data columns based on children */}
      {React.Children.map(children, field => {
        return (
          <TableCell key={`${id}-${field.props.source}`}>
            {React.cloneElement(field, {
              record,
              basePath,
              resource,
              reference:
                record && record.categoryOrSubcategoryId >= 10000
                  ? 'subcategories'
                  : 'categories'
            })}
          </TableCell>
        );
      })}
    </TableRow>
  );
};

const CustomDatagridBody = props => (
  <DatagridBody {...props} row={<CustomDatagridRow />} />
);

const CustomDatagrid = ({ isExpand, ...rest }) => (
  <Datagrid {...rest} body={<CustomDatagridBody />} />
);

const GroupCategoryDatagrid = props => {
  const {
    handleGroupCategoryClick,
    handleCategoryEditButtonClick,
    classes,
    expand,
    permissions,
    hasEdit,
    hasList,
    hasShow,
    subcategories,
    hasBulkActions,
    ...dataGridProps
  } = props;
  //unselecting all the checkboxes from the deleting actions in the ComponenWillUnmount listener
  const unSelectAll = useUnselectAll(dataGridProps.resource);

  useEffect(() => () => unSelectAll(), [unSelectAll]);

  const hasReadCategoryPermission = useMemo(() => {
    return permissions.includes('AUTH_READ_CATEGORY_NA');
  }, [permissions]);

  if (props.total > 0) {
    return (
      <>
        {expand && (
          <span className={classes.label}>
            Related Categories and Subcategories
          </span>
        )}
        <CustomDatagrid
          {...dataGridProps}
          classes={{ thead: expand ? classes.dataGridHead : '' }}
          isExpand={expand}
          hasBulkActions={hasBulkActions && !expand}
          dispatch={null}
        >
          <ReferenceField
            source="categoryOrSubcategoryId"
            reference="categories"
            label="Category or Subcategory Name"
            sortable={false}
            link={false}
          >
            <FunctionField
              render={record =>
                !hasReadCategoryPermission ||
                record.id >= 10000 ||
                record.subType === 'ADMIN' ? (
                  record.name
                ) : (
                  <LinkField
                    record={record}
                    source="name"
                    basePath="/categories"
                  />
                )
              }
            />
          </ReferenceField>
          <ReferenceField
            source="categoryOrSubcategoryId"
            reference="categories"
            label="Description"
            sortable={false}
            link={false}
          >
            <TextField label="Description" source="description" />
          </ReferenceField>

          <FunctionField
            label="Type"
            render={record =>
              record.categoryOrSubcategoryId >= 10000
                ? 'Subcategory'
                : 'Category'
            }
          />
          <ReferenceField
            source="categoryOrSubcategoryId"
            reference="categories"
            label="Status"
            sortable={false}
            link={false}
            headerClassName={classes.statusColumnStyle}
          >
            <StatusSelectField
              label="Status"
              source="status"
              choices={categoryStatus}
            />
          </ReferenceField>
        </CustomDatagrid>
      </>
    );
  } else if (props.loaded) {
    return (
      <span className={expand ? classes.expandLabel : classes.label}>
        There are no related Categories to be displayed
      </span>
    );
  } else {
    return (
      <div className={classes.progress}>
        <CircularProgress />
      </div>
    );
  }
};

const mapStateToProps = (state, props) => {
  return {
    subcategories: Object.values(
      get(state, 'admin.resources.subcategories.data', {})
    )
  };
};

export default connect(
  mapStateToProps,
  null
)(withStyles(styles)(GroupCategoryDatagrid));
